/**
 * SupplierChangeNotifications — /suppliers/change-notifications (Supplier S3).
 *
 * The supplier change-notification (SCN) workflow: suppliers notify us of
 * changes (process / site / material / spec / equipment / packaging); QA runs
 * them through RECEIVED → UNDER_ASSESSMENT → APPROVED | REJECTED, with
 * requalification flagged for site/material changes.
 */
import { useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { PageHero, StatCard, Stagger, Skeleton, EmptyState, Button } from '@edms/design-system';
import { useScnQuery, useScnTransition, type Scn, type ScnStatus } from './useScnQuery';
import styles from './SupplierChangeNotifications.module.css';

const STATUS_CLASS: Record<ScnStatus, string> = {
  RECEIVED: 'stReceived', UNDER_ASSESSMENT: 'stAssess', APPROVED: 'stApproved', REJECTED: 'stRejected',
};

export function SupplierChangeNotifications() {
  const { t, i18n } = useTranslation();
  const q = useScnQuery();
  const transition = useScnTransition();
  const dateFmt = useMemo(() => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }), [i18n.language]);
  const items = q.data?.items ?? [];

  function act(scnId: string, to: ScnStatus) {
    transition.mutate({ scnId, to });
  }

  return (
    <div className={styles.screen}>
      <PageHero
        eyebrow={t('scn.eyebrow', { defaultValue: 'Suppliers' })}
        title={t('scn.title', { defaultValue: 'Change notifications' })}
        subtitle={t('scn.subtitle', { defaultValue: 'Supplier-initiated changes, triaged through assessment to approval — with requalification flagged for site and material changes.' })}
      />

      {q.isLoading && (
        <>
          <div className={styles.kpiStrip} aria-hidden="true">{[0,1,2,3].map((i)=><Skeleton key={i} height={104} radius={14} />)}</div>
          {[0,1,2].map((i)=><Skeleton key={i} height={72} radius={10} />)}
        </>
      )}
      {q.isError && (
        <div className={styles.errorBanner} role="alert">
          <span>{t('scn.error', { defaultValue: 'Could not load change notifications.' })}</span>
          <Button variant="secondary" size="sm" onClick={() => q.refetch()}>{t('common.retry', { defaultValue: 'Retry' })}</Button>
        </div>
      )}

      {q.data && (
        <>
          <Stagger className={styles.kpiStrip} step={50}>
            <StatCard label={t('scn.kpi.total', { defaultValue: 'Notifications' })} value={q.data.summary.total} accent="var(--spectro-purple, #9333EA)" />
            <StatCard label={t('scn.kpi.received', { defaultValue: 'Awaiting triage' })} value={q.data.summary.received} accent={q.data.summary.received > 0 ? 'var(--color-status-warning, #D97706)' : 'var(--color-status-success, #16A34A)'} />
            <StatCard label={t('scn.kpi.assess', { defaultValue: 'Under assessment' })} value={q.data.summary.under_assessment} accent="var(--spectro-blue, #0065FF)" />
            <StatCard label={t('scn.kpi.approved', { defaultValue: 'Approved' })} value={q.data.summary.approved} accent="var(--color-status-success, #16A34A)" deltaCaption={t('scn.kpi.rejected', { defaultValue: '{{n}} rejected', n: q.data.summary.rejected })} />
          </Stagger>

          {items.length === 0 ? (
            <EmptyState icon="📨" title={t('scn.emptyTitle', { defaultValue: 'No change notifications' })} description={t('scn.empty', { defaultValue: 'No supplier change notifications have been received.' })} />
          ) : (
            <ul className={styles.list}>
              {items.map((s: Scn) => (
                <li key={s.scn_id} className={styles.row}>
                  <div className={styles.rowMain}>
                    <span className={styles.scnId}>{s.scn_id}</span>
                    <span className={`${styles.impact} ${s.impact === 'MAJOR' ? styles.impMajor : styles.impMinor}`}>{s.impact}</span>
                    <span className={styles.typeBadge}>{s.change_type.replace(/_/g, ' ')}</span>
                    <span className={styles.scnTitle}>{s.title}</span>
                    {s.requires_requal && <span className={styles.requal}>requal</span>}
                    <span className={`${styles.statusPill} ${styles[STATUS_CLASS[s.status]]}`}>{s.status.replace(/_/g, ' ')}</span>
                  </div>
                  <div className={styles.rowMeta}>
                    <Link to="/suppliers/$id" params={{ id: s.supplier_id }} className={styles.supLink}>{s.supplier_name}</Link>
                    <span className={styles.metaItem}>{t('scn.raised', { defaultValue: 'Raised {{d}}', d: dateFmt.format(new Date(s.raised_at_utc)) })}</span>
                    <span className={styles.metaItem}>{t('scn.sla', { defaultValue: 'SLA {{d}}', d: dateFmt.format(new Date(s.sla_due_at_utc)) })}</span>
                    <span className={styles.actions}>
                      {s.status === 'RECEIVED' && (
                        <button type="button" className={`${styles.actBtn} ${styles.actPrimary}`} disabled={transition.isPending} onClick={() => act(s.scn_id, 'UNDER_ASSESSMENT')}>
                          {t('scn.assess', { defaultValue: 'Start assessment' })}
                        </button>
                      )}
                      {s.status === 'UNDER_ASSESSMENT' && (
                        <>
                          <button type="button" className={`${styles.actBtn} ${styles.actReject}`} disabled={transition.isPending} onClick={() => act(s.scn_id, 'REJECTED')}>
                            {t('scn.reject', { defaultValue: 'Reject' })}
                          </button>
                          <button type="button" className={`${styles.actBtn} ${styles.actApprove}`} disabled={transition.isPending} onClick={() => act(s.scn_id, 'APPROVED')}>
                            {t('scn.approve', { defaultValue: 'Approve' })}
                          </button>
                        </>
                      )}
                      {(s.status === 'APPROVED' || s.status === 'REJECTED') && (
                        <button type="button" className={styles.actUndo} disabled={transition.isPending} onClick={() => act(s.scn_id, 'UNDER_ASSESSMENT')}>
                          {t('scn.reopen', { defaultValue: 'Reopen' })}
                        </button>
                      )}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <footer className={styles.disclaimer}>
            {t('scn.disclaimer', { defaultValue: 'Approving a site or material change should trigger supplier requalification + a linked change control. Advisory workflow at prototype fidelity.' })}
          </footer>
        </>
      )}
    </div>
  );
}
